<template>
  <div class="h-full">
    <!-- Compact Mode -->
    <div v-if="!expanded" class="h-full flex flex-col">
      <div>
        <p class="text-4xl font-bold text-emerald-600">{{ compactSummary.total?.toLocaleString() || '—' }}</p>
        <p class="text-sm text-gray-600">engagements</p>
        <p class="text-xs text-gray-400 mt-1">{{ filterDescription }}</p>
      </div>
      <!-- Compact Chart - taller when fullWidth -->
      <div v-if="chartData" class="flex-1 min-h-[100px]">
        <ChartV4
          :key="`compact-velocity-${fullWidth}`"
          :chart_data="chartData"
          :chart_config="fullWidth ? fullWidthCompactChartConfig : compactChartConfig"
          :overrideHeight="fullWidth ? 'h-[160px]' : 'h-[100px]'"
        />
      </div>
      <div v-else-if="loading" class="flex-1 flex items-center justify-center">
        <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    </div>

    <!-- Expanded Mode -->
    <div v-else>
    <!-- Tier 1: Primary Controls -->
    <div class="flex flex-wrap items-center gap-4 mb-4">
      <!-- Scope Toggle -->
      <div class="flex bg-gray-100 rounded-lg p-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="portfolioFilter = tab.id"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            portfolioFilter === tab.id
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Entity Scope (when All Practices selected) -->
      <div v-if="scopeEntityOptions.length > 0" class="flex items-center gap-2">
        <select
          v-model="selectedEntityId"
          @change="fetchAnalytics"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
        >
          <option :value="null">All Entities</option>
          <optgroup v-for="type in uniqueEntityTypes" :key="type" :label="type">
            <option v-for="entity in scopeEntityOptions.filter(e => e.type === type)" :key="entity.id" :value="entity.id" class="sensitive" data-spoof-kind="practice">
              {{ entity.name }}
            </option>
          </optgroup>
        </select>
      </div>

      <!-- Time Range Toggle -->
      <div class="flex bg-gray-100 rounded-lg p-1">
        <button
          v-for="range in timeRangeOptions"
          :key="range.value"
          @click="setTimeRange(range.value)"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            activeTimeRange === range.value
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          ]"
        >
          {{ range.label }}
        </button>
      </div>

      <!-- Practice Tags (inline grouped filter) -->
      <GroupedInfoTagFilter
        v-model="infoTagGroups"
        v-model:groupsMode="infoTagGroupsMode"
        label="Filter by Practice Tag"
        buttonLabel="Tag"
        color="emerald"
        direction="horizontal"
        @change="fetchAnalytics"
      />

      <!-- Advanced Toggle -->
      <button
        @click="showAdvanced = !showAdvanced"
        :class="[
          'p-2 rounded-lg transition-colors',
          showAdvanced ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100 text-gray-500'
        ]"
        title="Advanced filters"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
      </button>

    </div>

    <!-- Tier 2: Active Filter Chips -->
    <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-4">
      <Tag
        v-if="selectedStatuses.length > 0"
        :label="`Status: ${selectedStatuses.join(', ')}`"
        color="primary"
        :removable="true"
        @remove="selectedStatuses = []; fetchAnalytics()"
      />
      <Tag
        v-if="selectedEngagementChannels.length > 0"
        :label="`Channels: ${selectedEngagementChannels.length}`"
        color="primary"
        :removable="true"
        @remove="selectedEngagementChannels = []; fetchAnalytics()"
      />
      <Tag
        v-if="selectedUserRoles.length > 0"
        :label="`Roles: ${selectedUserRoles.join(', ')}`"
        color="primary"
        :removable="true"
        @remove="selectedUserRoles = []; fetchAnalytics()"
      />
      <Tag
        v-if="selectedContracts.length > 0"
        :label="`Contracts: ${selectedContracts.length}`"
        color="primary"
        :removable="true"
        @remove="selectedContracts = []; fetchAnalytics()"
      />
      <Tag
        v-if="selectedEngagementTypes.length > 0"
        :label="`Types: ${selectedEngagementTypes.length}`"
        color="purple"
        :removable="true"
        @remove="selectedEngagementTypes = []; fetchAnalytics()"
      />
      <Tag
        v-if="selectedEngagementTopics.length > 0"
        :label="`Topics: ${selectedEngagementTopics.length}`"
        color="purple"
        :removable="true"
        @remove="selectedEngagementTopics = []; fetchAnalytics()"
      />
      <Tag
        v-if="selectedEntityId"
        :label="`Scope: ${scopeEntityOptions.find(e => e.id === selectedEntityId)?.name}`"
        color="emerald"
        :removable="true"
        @remove="selectedEntityId = null; fetchAnalytics()"
      />
      <Tag
        v-if="filters.group_by !== 'total'"
        :label="`Group: ${groupByOptions.find(o => o.value === filters.group_by)?.label}`"
        color="gray"
        :removable="true"
        @remove="filters.group_by = 'total'; fetchAnalytics()"
      />
      <Tag
        v-if="filters.metric !== 'engagement_count'"
        :label="metricOptions.find(o => o.value === filters.metric)?.label"
        color="gray"
        :removable="true"
        @remove="filters.metric = 'engagement_count'; fetchAnalytics()"
      />
      <button
        @click="clearAllFilters"
        class="text-sm text-gray-500 hover:text-gray-700 ml-2"
      >
        Clear all
      </button>
    </div>

    <!-- Tier 3: Advanced Panel -->
    <div v-if="showAdvanced" ref="advancedPanel" class="p-4 bg-gray-50 rounded-lg mb-4 border border-gray-200">

      <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
        <!-- Status -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</label>
          <div class="space-y-1">
            <label v-for="opt in statusOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedStatuses" @change="fetchAnalytics" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Channel -->
        <div v-if="engagementChannelOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Channel</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in engagementChannelOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedEngagementChannels" @change="fetchAnalytics" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- User Role -->
        <div v-if="userRoleOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">User Role</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in userRoleOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedUserRoles" @change="fetchAnalytics" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Engagement Type -->
        <div v-if="engagementTypeOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Engagement Type</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in engagementTypeOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedEngagementTypes" @change="fetchAnalytics" class="mr-2 text-purple-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Engagement Topic -->
        <div v-if="engagementTopicOptions.length > 0">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Interaction Topic</label>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <label v-for="opt in engagementTopicOptions" :key="opt.value" class="flex items-center text-sm cursor-pointer">
              <input type="checkbox" :value="opt.value" v-model="selectedEngagementTopics" @change="fetchAnalytics" class="mr-2 text-primary-600 rounded">
              {{ opt.label }}
            </label>
          </div>
        </div>

      </div>

      <!-- Display Controls -->
      <div class="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-3 gap-6">
        <!-- Group By -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Group By</label>
          <select
            v-model="filters.group_by"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
            @change="handleGroupByChange"
          >
            <option v-for="option in groupByOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Metric -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Metric</label>
          <select
            v-model="filters.metric"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
            @change="fetchAnalytics"
          >
            <option v-for="option in metricOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Custom Date Range -->
        <div>
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Custom Date Range</label>
          <div class="flex items-center gap-2">
            <input
              v-model="filters.start_date"
              type="date"
              class="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
              @change="activeTimeRange = 'custom'; fetchAnalytics()"
            />
            <span class="text-gray-500">to</span>
            <input
              v-model="filters.end_date"
              type="date"
              class="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
              @change="activeTimeRange = 'custom'; fetchAnalytics()"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <LoadingIndicator v-if="loading" message="Loading analytics data..." />

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-600">{{ error }}</p>
    </div>

    <!-- Chart Display -->
    <div v-else-if="chartData" style="height: 500px;">
      <ChartV4
        :key="`velocity-chart-${fullWidth}`"
        :chart_data="chartData"
        :chart_config="chartConfig"
        :overrideHeight="'h-[500px]'"
      />
    </div>

    <!-- Group By Legend/Key -->
    <div v-if="filters.group_by !== 'total' && chartData && chartData.series" class="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h4 class="text-sm font-semibold text-gray-700 mb-3">
        {{ groupByOptions.find(o => o.value === filters.group_by)?.label || 'Group' }} Breakdown
      </h4>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        <div
          v-for="series in chartData.series"
          :key="series.config.label"
          class="flex items-center gap-2 p-2 bg-white rounded border border-gray-200"
        >
          <div
            class="w-4 h-4 rounded shrink-0"
            :style="{ backgroundColor: series.config.backgroundColor }"
          ></div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate" :title="series.config.label">
              {{ series.config.label }}
            </div>
            <div class="text-xs text-gray-500">
              {{ Math.round(calculateSeriesTotal(series)).toLocaleString() }} {{ getMetricLabel().toLowerCase() }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Stats (simplified) -->
    <div v-if="analyticsData && analyticsData.summary" class="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="text-center p-4 bg-gray-50 rounded-lg">
        <p class="text-3xl font-bold text-gray-900">{{ analyticsData.summary.total_engagements?.toLocaleString() }}</p>
        <p class="text-sm text-gray-500">engagements</p>
      </div>
      <div class="text-center p-4 bg-gray-50 rounded-lg">
        <p class="text-3xl font-bold text-gray-900">{{ analyticsData.summary.unique_practices?.toLocaleString() }}</p>
        <p class="text-sm text-gray-500">practices</p>
      </div>
      <div class="text-center p-4 bg-gray-50 rounded-lg">
        <p class="text-3xl font-bold text-gray-900">{{ analyticsData.summary.unique_users?.toLocaleString() }}</p>
        <p class="text-sm text-gray-500">users</p>
      </div>
      <div class="text-center p-4 bg-gray-50 rounded-lg">
        <p class="text-3xl font-bold text-gray-900">{{ analyticsData.summary.avg_per_day }}</p>
        <p class="text-sm text-gray-500">per day</p>
      </div>
    </div>

    <!-- Raw Data Toggle and Grid -->
    <div v-if="analyticsData && analyticsData.data" class="mt-6">
      <div class="flex items-center justify-between mb-4">
        <button
          @click="showRawData = !showRawData"
          class="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          <svg
            :class="['w-4 h-4 transition-transform', showRawData ? 'rotate-90' : '']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
          <span class="text-sm font-medium">{{ showRawData ? 'Hide' : 'Show' }} Raw Data ({{ analyticsData.data_count }} rows)</span>
        </button>

        <ExportMenu v-if="showRawData" :exports="[{ type: 'excel', action: exportToExcel }]" />
      </div>

      <!-- AG Grid Container -->
      <div v-show="showRawData" class="border border-gray-200 rounded-lg overflow-hidden">
        <ag-grid-vue
          style="height: 400px;"
          :theme="standardListTheme"
          :columnDefs="columnDefs"
          :rowData="gridRowData"
          :defaultColDef="defaultColDef"
          :enableCellTextSelection="true"
          :suppressRowClickSelection="true"
          :rowSelection="'multiple'"
          :animateRows="true"
          :pagination="true"
          :paginationPageSize="20"
          @grid-ready="onGridReady"
        >
        </ag-grid-vue>
      </div>
    </div>
    </div><!-- /expanded -->
  </div>
</template>

<script>
import axios from 'axios'
import ChartV4 from '@/components/widgetcomponents/Viz_Elements/Chart_V4.vue'
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue'
import GroupedInfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/GroupedInfoTagFilter.vue'
import { AgGridVue } from 'ag-grid-vue3'
import { exportToExcel } from '@/utils/exportArraytoXlsx.js'
import { standardListTheme } from '@/utils/common.js'
import { canAccessFeature } from '@/utils/access'
import { FEATURES } from '@/constants/features'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'

export default {
  components: {
    ChartV4,
    SimpleMultiSelect,
    GroupedInfoTagFilter,
    AgGridVue,
    ExportMenu,
    LoadingIndicator,
    Tag
  },
  props: {
    settings: {
      type: Object,
      default: () => ({})
    },
    sectionId: {
      type: String,
      default: null
    },
    expanded: {
      type: Boolean,
      default: true
    },
    fullWidth: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      analyticsData: null,
      chartData: null,
      showRawData: false,
      standardListTheme: standardListTheme,
      gridApi: null,
      gridColumnApi: null,
      gridRowData: [],
      columnDefs: [],
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        minWidth: 100,
        flex: 1
      },
      selectedStatuses: [],
      selectedUserRoles: [],
      selectedHasTranscript: [],
      selectedHasNotes: [],
      selectedEngagementChannels: [],
      selectedContracts: [],
      contractOptions: [],
      selectedEntityId: null,
      scopeEntityOptions: [],
      portfolioFilter: undefined, // 'all' or 'my', set on mounted to first available tab
      showAdvanced: false,
      activeTimeRange: '90d',
      timeRangeOptions: [
        { value: '7d', label: '7d' },
        { value: '30d', label: '30d' },
        { value: '90d', label: '90d' },
        { value: 'ytd', label: 'YTD' }
      ],
      filters: {
        start_date: this.getDefaultStartDate(),
        end_date: this.getDefaultEndDate(),
        group_by: 'total',
        time_period: 'day',
        metric: 'engagement_count'
      },
      statusOptions: [
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'CANCELLED', label: 'Cancelled' },
        { value: 'NO_SHOW', label: 'No Show' },
        { value: 'SCHEDULED', label: 'Scheduled' }
      ],
      userRoleOptions: [],
      binaryOptions: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
      ],
      engagementChannelOptions: [],
      engagementTypeOptions: [],
      selectedEngagementTypes: [],
      engagementTopicOptions: [],
      selectedEngagementTopics: [],
      // Grouped InfoTag filter state — array of {include, mode, exclude}
      // combined per `infoTagGroupsMode` (default 'or').
      infoTagGroups: [],
      infoTagGroupsMode: 'or',
      infoTagOptions: [],
      groupByOptions: [
        { value: 'total', label: 'Total Only' },
        { value: 'user_role', label: 'By Role' },
        { value: 'status', label: 'By Status' },
        { value: 'engagement_type', label: 'By Type' },
        { value: 'appuser', label: 'By User' },
        { value: 'has_transcript', label: 'By Transcript' },
        { value: 'has_notes', label: 'By Notes' },
        { value: 'engagement_channel', label: 'By Channel' },
        { value: 'engagement_topic', label: 'By Topic' }
      ],
      timePeriodOptions: [
        { value: 'day', label: 'By Day' },
        { value: 'week', label: 'By Week' },
        { value: 'month', label: 'By Month' },
        { value: 'ytd', label: 'Year to Date' }
      ],
      metricOptions: [
        { value: 'engagement_count', label: 'Engagements' },
        { value: 'minutes', label: 'Minutes' },
        { value: 'practices', label: 'Unique Practices' },
        { value: 'avg_engagement_score', label: 'Avg Score' }
      ],
      colorPalettes: {
        user_role: {},
        status: {
          'COMPLETED': '#10B981',
          'CANCELLED': '#EF4444',
          'NO_SHOW': '#F59E0B',
          'SCHEDULED': '#4F46E5'
        },
        has_transcript: {
          'Yes': '#8B5CF6',
          'No': '#9CA3AF'
        },
        has_notes: {
          'Yes': '#14B8A6',
          'No': '#9CA3AF'
        },
        engagement_type: {
          'Meaningful': '#10B981',
          'Routine': '#4F46E5',
          'None': '#9CA3AF'
        }
      },
      chartConfig: {
        chartType: 'vertical_bar',
        title: 'Engagement Analytics',
        margin: { top: 80, right: 180, bottom: 120, left: 80 },
        xAxis: {
          labelRotation: -45,
          fontSize: 12,
          tickFormat: null
        },
        yAxes: [
          {
            position: 'left',
            fontSize: 12,
            tickFormat: null
          }
        ],
        legend: {
          display: false,
          position: 'right',
          spacing: 15
        },
        formatting: {
          percentage: false,
          sig_figs: 2
        },
        labelDisplayConfig: {
          maxLabels: 40,
          minLabelSpacing: 5,
          labelFontSize: 12
        }
      },
      compactChartConfig: {
        chartType: 'vertical_bar',
        title: '',
        margin: { top: 5, right: 5, bottom: 25, left: 35 },
        xAxis: {
          labelRotation: 0,
          fontSize: 8
        },
        yAxes: [{ position: 'left', fontSize: 8 }],
        legend: { display: false },
        formatting: { percentage: false, sig_figs: 0 },
        labelDisplayConfig: { maxLabels: 12, minLabelSpacing: 5, labelFontSize: 8 }
      },
      fullWidthCompactChartConfig: {
        chartType: 'vertical_bar',
        title: '',
        margin: { top: 10, right: 20, bottom: 40, left: 50 },
        xAxis: {
          labelRotation: -45,
          fontSize: 10
        },
        yAxes: [{ position: 'left', fontSize: 10 }],
        legend: { display: false },
        formatting: { percentage: false, sig_figs: 0 },
        labelDisplayConfig: { maxLabels: 30, minLabelSpacing: 5, labelFontSize: 10 }
      }
    }
  },
  computed: {
    isMyPractices() {
      return this.portfolioFilter === 'my'
    },
    tabs() {
      return [
        ...(canAccessFeature(FEATURES.COMPONENT_PRACTICE_PARTNER_ALL)
          ? [{ id: 'all', name: 'All Practices' }]
          : []),
        {
          id: 'my',
          name: 'My Practices'
        }
      ]
    },
    compactSummary() {
      if (!this.analyticsData?.summary) return {}
      return {
        total: this.analyticsData.summary.total_engagements,
        practices: this.analyticsData.summary.unique_practices
      }
    },
    filterPersistKey() {
      const tenantSlug = this.$store.getters['tenant/current_tenant_slug'] || 'default'
      return `${tenantSlug}_${this.settings.persist_key || 'engagement_velocity'}_filters`
    },
    filterDescription() {
      const parts = []

      // Portfolio scope
      if (this.portfolioFilter === 'my') {
        parts.push('My practices')
      } else {
        parts.push('All practices')
      }

      // InfoTag group filters (Implementation Status)
      const infoTagSummary = this.infoTagGroupsSummary
      if (infoTagSummary) {
        parts.push(infoTagSummary)
      }

      // Date range
      const startDate = new Date(this.filters.start_date)
      const endDate = new Date(this.filters.end_date)
      const daysDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
      if (daysDiff <= 7) {
        parts.push('Last 7 days')
      } else if (daysDiff <= 30) {
        parts.push('Last 30 days')
      } else if (daysDiff <= 100) {
        parts.push('Last ' + daysDiff + ' days')
      } else {
        parts.push(`${this.filters.start_date} to ${this.filters.end_date}`)
      }

      // Advanced filters
      if (this.selectedStatuses.length > 0) {
        parts.push('Status: ' + this.selectedStatuses.join(', '))
      }
      if (this.selectedEngagementChannels.length > 0) {
        parts.push(this.selectedEngagementChannels.length + ' channel' + (this.selectedEngagementChannels.length > 1 ? 's' : ''))
      }
      if (this.selectedUserRoles.length > 0) {
        parts.push('Roles: ' + this.selectedUserRoles.join(', '))
      }
      if (this.selectedContracts.length > 0) {
        parts.push(this.selectedContracts.length + ' contract' + (this.selectedContracts.length > 1 ? 's' : ''))
      }
      if (this.selectedEngagementTypes.length > 0) {
        parts.push(this.selectedEngagementTypes.length + ' type' + (this.selectedEngagementTypes.length > 1 ? 's' : ''))
      }
      if (this.selectedEntityId) {
        const entity = this.scopeEntityOptions.find(e => e.id === this.selectedEntityId)
        if (entity) parts.push('Scope: ' + entity.name)
      }
      if (this.filters.group_by !== 'total') {
        const groupLabel = this.groupByOptions.find(o => o.value === this.filters.group_by)?.label
        if (groupLabel) parts.push(groupLabel)
      }
      if (this.filters.metric !== 'engagement_count') {
        const metricLabel = this.metricOptions.find(o => o.value === this.filters.metric)?.label
        if (metricLabel) parts.push(metricLabel)
      }

      return parts.join(' · ')
    },
    hasActiveFilters() {
      return this.selectedStatuses.length > 0 ||
             this.selectedEngagementChannels.length > 0 ||
             this.selectedUserRoles.length > 0 ||
             this.selectedContracts.length > 0 ||
             this.selectedEngagementTypes.length > 0 ||
             this.selectedEngagementTopics.length > 0 ||
             this.activeInfoTagGroups.length > 0 ||
             this.selectedEntityId !== null ||
             this.filters.group_by !== 'total' ||
             this.filters.metric !== 'engagement_count'
    },
    activeInfoTagGroups() {
      return (this.infoTagGroups || []).filter(g =>
        (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
      )
    },
    infoTagGroupsSummary() {
      const groups = this.activeInfoTagGroups
      if (groups.length === 0) return ''
      const tagName = (id) => this.infoTagOptions.find(t => t.id === id)?.name || id
      const groupSummaries = groups.map(g => {
        const inc = (g.include || []).map(tagName)
        const exc = (g.exclude || []).map(t => `NOT ${tagName(t)}`)
        const joiner = g.mode === 'and' ? ' AND ' : ' OR '
        return [...inc, ...exc].join(joiner)
      })
      if (groupSummaries.length === 1) return groupSummaries[0]
      const between = this.infoTagGroupsMode === 'and' ? ' AND ' : ' OR '
      return groupSummaries.map(s => `(${s})`).join(between)
    },
    uniqueEntityTypes() {
      return [...new Set(this.scopeEntityOptions.map(e => e.type))]
    }
  },
  methods: {
    applyTimeRange(preset) {
      // Calculate dates from preset without fetching data
      this.activeTimeRange = preset
      const end = new Date()
      let start = new Date()

      switch(preset) {
        case '7d':
          start.setDate(end.getDate() - 7)
          this.filters.time_period = 'day'
          break
        case '30d':
          start.setDate(end.getDate() - 30)
          this.filters.time_period = 'day'
          break
        case '90d':
          start.setDate(end.getDate() - 90)
          this.filters.time_period = 'week'
          break
        case 'ytd':
          start = new Date(end.getFullYear(), 0, 1)
          this.filters.time_period = 'month'
          break
      }

      this.filters.start_date = start.toISOString().split('T')[0]
      this.filters.end_date = end.toISOString().split('T')[0]
    },

    setTimeRange(preset) {
      this.applyTimeRange(preset)
      this.fetchAnalytics()
    },

    clearAllFilters() {
      this.selectedStatuses = []
      this.selectedEngagementChannels = []
      this.selectedUserRoles = []
      this.selectedContracts = []
      this.selectedHasTranscript = []
      this.selectedHasNotes = []
      this.selectedEngagementTypes = []
      this.selectedEngagementTopics = []
      this.infoTagGroups = []
      this.infoTagGroupsMode = 'or'
      this.selectedEntityId = null
      this.filters.group_by = 'total'
      this.filters.metric = 'engagement_count'
      this.fetchAnalytics()
    },

    saveFilters() {
      const filterState = {
        filters: this.filters,
        activeTimeRange: this.activeTimeRange,
        selectedStatuses: this.selectedStatuses,
        selectedUserRoles: this.selectedUserRoles,
        selectedHasTranscript: this.selectedHasTranscript,
        selectedHasNotes: this.selectedHasNotes,
        selectedEngagementChannels: this.selectedEngagementChannels,
        selectedContracts: this.selectedContracts,
        selectedEngagementTypes: this.selectedEngagementTypes,
        selectedEngagementTopics: this.selectedEngagementTopics,
        infoTagGroups: this.infoTagGroups,
        infoTagGroupsMode: this.infoTagGroupsMode,
        selectedEntityId: this.selectedEntityId,
        portfolioFilter: this.portfolioFilter
      }
      localStorage.setItem(this.filterPersistKey, JSON.stringify(filterState))
    },

    loadFilters() {
      // 1. Apply baseline from page config (if provided)
      if (this.settings.default_filters) {
        const baseline = this.settings.default_filters
        if (baseline.group_by) this.filters.group_by = baseline.group_by
        if (baseline.time_period) this.filters.time_period = baseline.time_period
        if (baseline.metric) this.filters.metric = baseline.metric
        if (baseline.portfolioFilter) this.portfolioFilter = baseline.portfolioFilter
        if (baseline.infoTagGroups) {
          this.infoTagGroups = baseline.infoTagGroups
        } else if (baseline.selectedInfoTags) {
          this.infoTagGroups = [{
            include: baseline.selectedInfoTags,
            mode: baseline.infoTagFilterMode || 'or',
            exclude: baseline.excludedInfoTags || [],
          }]
        }
        if (baseline.infoTagGroupsMode) this.infoTagGroupsMode = baseline.infoTagGroupsMode
        if (baseline.selectedEngagementTypes) this.selectedEngagementTypes = baseline.selectedEngagementTypes
        if (baseline.activeTimeRange) this.applyTimeRange(baseline.activeTimeRange)
      }

      // 2. Override with localStorage user customizations (if present)
      const stored = localStorage.getItem(this.filterPersistKey)
      if (stored) {
        try {
          const filterState = JSON.parse(stored)
          if (filterState.selectedStatuses) this.selectedStatuses = filterState.selectedStatuses
          if (filterState.selectedUserRoles) this.selectedUserRoles = filterState.selectedUserRoles
          if (filterState.selectedHasTranscript) this.selectedHasTranscript = filterState.selectedHasTranscript
          if (filterState.selectedHasNotes) this.selectedHasNotes = filterState.selectedHasNotes
          if (filterState.selectedEngagementChannels) this.selectedEngagementChannels = filterState.selectedEngagementChannels
          if (filterState.selectedContracts) this.selectedContracts = filterState.selectedContracts
          if (filterState.selectedEngagementTypes) this.selectedEngagementTypes = filterState.selectedEngagementTypes
          if (filterState.selectedEngagementTopics) this.selectedEngagementTopics = filterState.selectedEngagementTopics
          // InfoTag filter — prefer new grouped shape, migrate from legacy flat keys.
          if (filterState.infoTagGroups) {
            this.infoTagGroups = filterState.infoTagGroups
          } else if (
            (filterState.selectedInfoTags?.length || 0) > 0 ||
            (filterState.excludedInfoTags?.length || 0) > 0
          ) {
            this.infoTagGroups = [{
              include: filterState.selectedInfoTags || [],
              mode: filterState.infoTagFilterMode || 'or',
              exclude: filterState.excludedInfoTags || [],
            }]
          }
          if (filterState.infoTagGroupsMode === 'and' || filterState.infoTagGroupsMode === 'or') {
            this.infoTagGroupsMode = filterState.infoTagGroupsMode
          }
          if (filterState.selectedEntityId !== undefined) this.selectedEntityId = filterState.selectedEntityId
          if (filterState.portfolioFilter) this.portfolioFilter = filterState.portfolioFilter
          // Recalculate dates from activeTimeRange to ensure consistency
          if (filterState.activeTimeRange) {
            this.applyTimeRange(filterState.activeTimeRange)
          }
        } catch (e) {
          console.warn('Failed to load saved filters:', e)
        }
      }
    },

    resetToDefaults() {
      // Clear localStorage
      localStorage.removeItem(this.filterPersistKey)

      // Reset to widget defaults
      this.filters = {
        start_date: this.getDefaultStartDate(),
        end_date: this.getDefaultEndDate(),
        group_by: 'total',
        time_period: 'day',
        metric: 'engagement_count'
      }
      this.selectedStatuses = []
      this.selectedUserRoles = []
      this.selectedHasTranscript = []
      this.selectedHasNotes = []
      this.selectedEngagementChannels = []
      this.selectedContracts = []
      this.selectedEngagementTypes = []
      this.selectedEngagementTopics = []
      this.infoTagGroups = []
      this.infoTagGroupsMode = 'or'
      this.portfolioFilter = this.tabs[0]?.id || 'all'

      // Re-apply baseline from page config
      if (this.settings.default_filters) {
        const baseline = this.settings.default_filters
        if (baseline.group_by) this.filters.group_by = baseline.group_by
        if (baseline.time_period) this.filters.time_period = baseline.time_period
        if (baseline.metric) this.filters.metric = baseline.metric
        if (baseline.portfolioFilter) this.portfolioFilter = baseline.portfolioFilter
        if (baseline.infoTagGroups) {
          this.infoTagGroups = baseline.infoTagGroups
        } else if (baseline.selectedInfoTags) {
          this.infoTagGroups = [{
            include: baseline.selectedInfoTags,
            mode: baseline.infoTagFilterMode || 'or',
            exclude: baseline.excludedInfoTags || [],
          }]
        }
        if (baseline.infoTagGroupsMode) this.infoTagGroupsMode = baseline.infoTagGroupsMode
        if (baseline.selectedEngagementTypes) this.selectedEngagementTypes = baseline.selectedEngagementTypes
      }

      this.fetchAnalytics()
    },

    getDefaultStartDate() {
      const date = new Date()
      date.setDate(date.getDate() - 90)
      return date.toISOString().split('T')[0]
    },

    getDefaultEndDate() {
      return new Date().toISOString().split('T')[0]
    },

    handleGroupByChange() {
      this.fetchAnalytics()
    },

    handleTimePeriodChange() {
      const period = this.filters.time_period
      const endDate = new Date()
      let startDate = new Date()

      switch(period) {
        case 'day':
          startDate.setDate(endDate.getDate() - 30)
          break
        case 'week':
          startDate.setDate(endDate.getDate() - 90)
          break
        case 'month':
          startDate.setMonth(endDate.getMonth() - 12)
          break
        case 'ytd':
          startDate = new Date(endDate.getFullYear(), 0, 1)
          break
      }

      this.filters.start_date = startDate.toISOString().split('T')[0]
      this.filters.end_date = endDate.toISOString().split('T')[0]

      this.fetchAnalytics()
    },

    async fetchEngagementChannels() {
      try {
        const response = await axios.get('/api/engagementChannels/')
        if (response.data) {
          this.engagementChannelOptions = response.data.map(channel => ({
            value: channel.id.toString(),
            label: channel.name
          }))
        }
      } catch (error) {
        console.error('Error fetching engagement channels:', error)
      }
    },

    async fetchContracts() {
      try {
        const typesRes = await axios.get('/api/entity-types/')
        const types = typesRes.data.results || typesRes.data || []
        const contractType = types.find(t => t.type_name === 'Contract' || t.type_name === 'Contract Pool')
        if (!contractType) return
        const response = await axios.get('/api/entities/', {
          params: {
            entity_type_id: contractType.id,
          }
        })
        if (response.data) {
          this.contractOptions = response.data.map(contract => ({
            value: contract.id.toString(),
            label: contract.name
          }))
        }
      } catch (error) {
        console.error('Error fetching contracts:', error)
        this.contractOptions = []
      }
    },

    async fetchScopeEntities() {
      try {
        const params = {
          exclude_type: 'Practice',
          has_practice_children: 'true',
        }
        if (this.isMyPractices) {
          const currentUser = this.$store.state.auth.current_user
          if (currentUser && currentUser.id) {
            params.portfolio_user_id = currentUser.id
          }
        }
        const response = await axios.get('/api/entities/', { params })
        if (response.data) {
          this.scopeEntityOptions = response.data
            .map(entity => ({
              id: entity.id,
              name: entity.name,
              type: entity.entity_type_name
            }))
            .filter(e => e.type !== 'Contract Cohort')
            .sort((a, b) => {
              if (a.type !== b.type) return a.type.localeCompare(b.type)
              return a.name.localeCompare(b.name)
            })
        }
      } catch (error) {
        console.error('Error fetching scope entities:', error)
        this.scopeEntityOptions = []
      }
    },

    async fetchUserRoleOptions() {
      try {
        const response = await axios.get('/api/entity-types/', {
          params: {
            is_user_role: true
          }
        })
        if (response.data) {
          // Define color palette for user roles
          const roleColors = ['#4F46E5', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4']

          this.userRoleOptions = response.data.map((entityType, idx) => ({
            value: entityType.type_name,
            label: entityType.type_name
          }))

          // Build dynamic color palette
          this.colorPalettes.user_role = {}
          response.data.forEach((entityType, idx) => {
            this.colorPalettes.user_role[entityType.type_name] = roleColors[idx % roleColors.length]
          })

          // Always add "Other" option at the end
          this.userRoleOptions.push({ value: 'Other', label: 'Other' })
          this.colorPalettes.user_role['Other'] = '#9CA3AF'
        }
      } catch (error) {
        console.error('Error fetching user role options:', error)
        // If API fails or returns empty, don't show user role filter
        this.userRoleOptions = []
        this.colorPalettes.user_role = {}
      }
    },

    async fetchEngagementTypes() {
      try {
        const response = await axios.get('/api/engagementtypes/')
        if (response.data) {
          this.engagementTypeOptions = response.data.map(type => ({
            value: type.id,
            label: type.name
          }))
        }
      } catch (error) {
        console.error('Error fetching engagement types:', error)
        this.engagementTypeOptions = []
      }
    },

    async fetchEngagementTopics() {
      try {
        const response = await axios.get('/api/engagementtopics/')
        if (response.data) {
          this.engagementTopicOptions = response.data.map(topic => ({
            value: topic.id,
            label: topic.name
          }))
        }
      } catch (error) {
        console.error('Error fetching engagement topics:', error)
        this.engagementTopicOptions = []
      }
    },

    async fetchInfoTags() {
      try {
        const response = await axios.get('/api/infotags/', {
          params: { infoTagType__name: 'Implementation Status' }
        })
        if (response.data) {
          this.infoTagOptions = response.data.map(tag => ({
            id: tag.id,
            name: tag.name
          }))
        }
      } catch (error) {
        console.error('Error fetching info tags:', error)
        this.infoTagOptions = []
      }
    },

    async fetchAnalytics() {
      this.loading = true
      this.error = null

      try {
        let timeGranularity = 'day'
        if (this.filters.time_period === 'week') {
          timeGranularity = 'week'
        } else if (this.filters.time_period === 'month') {
          timeGranularity = 'month'
        } else if (this.filters.time_period === 'ytd') {
          timeGranularity = 'ytd'
        }

        const params = {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          group_by: this.filters.group_by,
          metric: this.filters.metric,
          time_granularity: timeGranularity
        }

        if (this.selectedStatuses.length > 0) {
          params.status_filters = this.selectedStatuses.join(',')
        }

        if (this.selectedUserRoles.length > 0) {
          params.user_role_filters = this.selectedUserRoles.join(',')
        }

        if (this.selectedHasTranscript.length > 0) {
          params.has_transcript_filters = this.selectedHasTranscript.join(',')
        }

        if (this.selectedHasNotes.length > 0) {
          params.has_notes_filters = this.selectedHasNotes.join(',')
        }

        if (this.selectedEngagementChannels.length > 0) {
          params.engagement_channel_filters = this.selectedEngagementChannels.join(',')
        }

        if (this.selectedContracts.length > 0) {
          params.contract_filters = this.selectedContracts.join(',')
        }

        if (this.selectedEngagementTypes.length > 0) {
          params.engagement_type_filters = this.selectedEngagementTypes.join(',')
        }

        if (this.selectedEngagementTopics.length > 0) {
          params.engagement_topic_filters = this.selectedEngagementTopics.join(',')
        }

        // Add InfoTag group filters
        const validGroups = (this.infoTagGroups || []).filter(g =>
          (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
        )
        if (validGroups.length > 0) {
          params.entity_infotag_groups = JSON.stringify({
            mode: this.infoTagGroupsMode,
            groups: validGroups,
          })
        }

        // Only filter by current user's portfolio if "My Practices" is selected
        if (this.portfolioFilter === 'my') {
          const currentUser = this.$store.state.auth.current_user
          if (currentUser && currentUser.id) {
            params.portfolio_user_id = currentUser.id
          }
        }

        // Add entity scope filter if selected
        if (this.selectedEntityId) {
          params.scope_entity_id = this.selectedEntityId
        }

        const response = await axios.get('/api/engagements/analytics/', { params })

        if (response.data.success) {
          this.analyticsData = response.data
          this.transformDataForChart()
          this.updateGridData()
        } else {
          this.error = response.data.error || 'Failed to fetch analytics data'
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error)
        this.error = error.response?.data?.error || 'Failed to fetch analytics data'
      } finally {
        this.loading = false
        this.saveFilters()
      }
    },

    updateGridData() {
      if (!this.analyticsData || !this.analyticsData.data) {
        this.gridRowData = []
        this.columnDefs = []
        return
      }

      this.gridRowData = this.analyticsData.data

      if (this.gridRowData.length > 0) {
        const firstRow = this.gridRowData[0]
        const cols = []

        Object.keys(firstRow).forEach(key => {
          const col = {
            field: key,
            headerName: this.formatHeaderName(key),
            width: 150,
            resizable: true,
            sortable: true,
            filter: true
          }

          if (key === 'date' || key === 'time_period') {
            col.valueFormatter = params => {
              if (!params.value) return ''
              return new Date(params.value).toLocaleDateString()
            }
          }

          if (key === 'count' || key.includes('total_') || key.includes('unique_') || key.includes('avg_')) {
            col.cellClass = 'text-right'
            col.width = 120
          }

          cols.push(col)
        })

        this.columnDefs = cols
      } else {
        this.columnDefs = []
      }
    },

    formatHeaderName(fieldName) {
      return fieldName
        .replace(/__/g, ' ')
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    transformDataForChart() {
      if (!this.analyticsData || !this.analyticsData.data) {
        this.chartData = null
        return
      }

      const data = this.analyticsData.data
      const groupBy = this.filters.group_by

      if (this.filters.metric === 'avg_engagement_score') {
        this.chartConfig.formatting.sig_figs = 2
      } else {
        this.chartConfig.formatting.sig_figs = 0
      }

      this.transformStackedTimeSeriesData(data, groupBy)
    },

    transformStackedTimeSeriesData(data, groupBy) {
      if (!data || data.length === 0) {
        this.chartData = null
        return
      }

      const metricFieldMap = {
        'engagement_count': 'count',
        'minutes': 'total_minutes',
        'practices': 'unique_practices',
        'avg_engagement_score': 'avg_engagement_score'
      }
      const valueField = metricFieldMap[this.filters.metric] || 'count'

      const timePeriods = [...new Set(data.map(d => d.time_period))].sort((a, b) => {
        return new Date(a) - new Date(b)
      })

      const labels = timePeriods.map(tp => this.formatDateLabel(tp))

      let series = []

      if (groupBy === 'total') {
        const values = timePeriods.map(tp => {
          const item = data.find(d => d.time_period === tp)
          const val = item ? item[valueField] : 0
          return (val !== null && val !== undefined && !isNaN(val)) ? Number(val) : 0
        })

        series = [{
          values: values,
          config: {
            label: this.getMetricLabel(),
            backgroundColor: '#4F46E5',
            type: 'bar',
            yAxis: 'left',
            offset: 0
          }
        }]

      } else {
        let categories = []
        let categoryKey = ''
        let colorMap = {}

        if (groupBy === 'user_role') {
          categoryKey = 'user_role'
          categories = [...new Set(data.map(d => d.user_role))].sort()
          colorMap = this.colorPalettes.user_role
        } else if (groupBy === 'status') {
          categoryKey = 'status'
          categories = [...new Set(data.map(d => d.status))].sort()
          colorMap = this.colorPalettes.status
        } else if (groupBy === 'has_transcript') {
          categoryKey = 'has_transcript'
          categories = ['Yes', 'No']
          colorMap = this.colorPalettes.has_transcript
        } else if (groupBy === 'has_notes') {
          categoryKey = 'has_notes'
          categories = ['Yes', 'No']
          colorMap = this.colorPalettes.has_notes
        } else if (groupBy === 'engagement_channel') {
          categoryKey = 'channel_name'
          categories = [...new Set(data.map(d => d.channel_name))].sort()
        } else if (groupBy === 'engagement_type') {
          categoryKey = 'engagement_type_name'
          categories = [...new Set(data.map(d => d.engagement_type_name))].sort()
          colorMap = this.colorPalettes.engagement_type
        } else if (groupBy === 'engagement_topic') {
          categoryKey = 'engagement_topics__name'
          categories = [...new Set(data.map(d => d.engagement_topics__name))].sort()
        } else if (groupBy === 'appuser') {
          const userMap = new Map()
          data.forEach(d => {
            const key = d.user__last_name && d.user__first_name
              ? `${d.user__last_name}, ${d.user__first_name}`
              : 'Unassigned'
            if (!userMap.has(key)) {
              userMap.set(key, d.user__id)
            }
          })
          categories = Array.from(userMap.keys()).sort()
          categoryKey = 'user_name'
        }

        series = categories.map((category, idx) => {
          const values = timePeriods.map(tp => {
            let item
            if (groupBy === 'appuser') {
              item = data.find(d => {
                const name = d.user__last_name && d.user__first_name
                  ? `${d.user__last_name}, ${d.user__first_name}`
                  : 'Unassigned'
                return d.time_period === tp && name === category
              })
            } else {
              item = data.find(d => d.time_period === tp && d[categoryKey] === category)
            }

            const val = item ? item[valueField] : 0
            return (val !== null && val !== undefined && !isNaN(val)) ? Number(val) : 0
          })

          const color = colorMap && colorMap[category]
            ? colorMap[category]
            : this.getColorForIndex(idx)

          return {
            values: values,
            config: {
              label: category || 'Unknown',
              backgroundColor: color,
              type: 'bar',
              yAxis: 'left',
              offset: 0,
              stack: 'main'
            }
          }
        })
      }

      this.chartData = {
        labels: labels,
        series: series
      }

      if (groupBy === 'user_role' || groupBy === 'status' || groupBy === 'has_transcript' ||
          groupBy === 'has_notes' || groupBy === 'engagement_channel' || groupBy === 'engagement_type') {
        this.chartConfig.legend.display = true
        this.chartConfig.legend.position = 'right'
      } else {
        this.chartConfig.legend.display = false
      }

      this.chartConfig.title = this.getChartTitle(groupBy)
    },

    getColorForIndex(idx) {
      const colors = [
        '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#14B8A6',
        '#6366F1', '#A855F7', '#D946EF', '#0EA5E9', '#22C55E'
      ]
      return colors[idx % colors.length]
    },

    getChartTitle(groupBy) {
      const metricLabel = this.getMetricLabel()
      const timePeriod = this.filters.time_period

      const groupLabels = {
        'total': '',
        'user_role': ' by Role',
        'status': ' by Status',
        'engagement_type': ' by Type',
        'appuser': ' by User',
        'has_transcript': ' by Transcript',
        'has_notes': ' by Notes',
        'engagement_channel': ' by Channel'
      }

      return `${metricLabel}${groupLabels[groupBy] || ''} (${timePeriod})`
    },

    formatDateLabel(dateStr) {
      if (!dateStr) return 'Invalid Date'

      const period = this.filters.time_period

      if (period === 'ytd') {
        const year = dateStr.split('-')[0]
        return `Year to Date ${year}`
      }

      let date
      if (typeof dateStr === 'string') {
        const isoStr = dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00Z`
        date = new Date(isoStr)
      } else {
        date = new Date(dateStr)
      }

      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }

      switch(period) {
        case 'day':
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
        case 'week':
          return `Week of ${date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })}`
        case 'month':
          return date.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
          })
        default:
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })
      }
    },

    getMetricLabel() {
      const labels = {
        'engagement_count': 'Engagements',
        'minutes': 'Minutes',
        'practices': 'Unique Practices',
        'avg_engagement_score': 'Avg Engagement Score'
      }
      return labels[this.filters.metric] || 'Value'
    },

    calculateSeriesTotal(series) {
      if (!series.values) return 0
      return series.values.reduce((sum, val) => sum + (val || 0), 0)
    },

    onGridReady(params) {
      this.gridApi = params.api
      this.gridColumnApi = params.columnApi

      if (this.gridColumnApi) {
        this.gridColumnApi.autoSizeAllColumns()
      }
    },

    exportToExcel() {
      if (!this.gridRowData || this.gridRowData.length === 0) {
        console.warn('No data available to export')
        return
      }

      const exportData = [...this.gridRowData]

      const summaryData = [
        {},
        { '--- SUMMARY ---': '' },
        { 'Date Range': `${this.filters.start_date} to ${this.filters.end_date}` },
        { 'Total Engagements': this.analyticsData.summary.total_engagements },
        { 'Unique Practices': this.analyticsData.summary.unique_practices },
        { 'Unique Users': this.analyticsData.summary.unique_users },
        { 'Total Minutes': Math.round(this.analyticsData.summary.total_minutes || 0) },
        { 'Average per Day': this.analyticsData.summary.avg_per_day },
        { 'Status Filters': this.selectedStatuses.join(', ') || 'All' },
        { 'Role Filters': this.selectedUserRoles.join(', ') || 'All' },
        { 'Transcript Filters': this.selectedHasTranscript.join(', ') || 'All' },
        { 'Notes Filters': this.selectedHasNotes.join(', ') || 'All' },
        { 'Channel Filters': this.selectedEngagementChannels.map(id => {
          const channel = this.engagementChannelOptions.find(c => c.value === id)
          return channel ? channel.label : id
        }).join(', ') || 'All' },
        { 'Group By': this.filters.group_by },
        { 'Time Period': this.filters.time_period },
        { 'Metric': this.filters.metric }
      ]

      const allData = [...exportData, ...summaryData]

      const filename = `engagement_analytics_${this.filters.group_by}_${new Date().toISOString().split('T')[0]}.xlsx`
      exportToExcel(allData, filename)
    }
  },

  watch: {
    portfolioFilter() {
      this.selectedEntityId = null
      this.fetchScopeEntities()
      this.fetchAnalytics()
    }
  },

  async mounted() {
    // Load saved filters before setting defaults
    this.loadFilters()

    // Only set default portfolio if not loaded from storage
    if (!this.portfolioFilter) {
      this.portfolioFilter = this.tabs[0].id
    }

    await Promise.all([
      this.fetchUserRoleOptions(),
      this.fetchEngagementChannels(),
      this.fetchContracts(),
      this.fetchEngagementTypes(),
      this.fetchEngagementTopics(),
      this.fetchInfoTags(),
      this.fetchScopeEntities()
    ])
    await this.fetchAnalytics()
  }
}
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

:deep(.text-right) {
  text-align: right;
}

:deep(.text-center) {
  text-align: center;
}

:deep(.ag-theme-alpine) {
  --ag-header-background-color: #f9fafb;
  --ag-header-foreground-color: #374151;
  --ag-border-color: #e5e7eb;
}

:deep(.ag-header-cell-label) {
  font-weight: 600;
  font-size: 0.875rem;
}

:deep(.ag-cell) {
  font-size: 0.875rem;
}
</style>
